import {
  BrandIcon,
  BrandIconId,
  BrandIconSize,
  Button,
  ButtonVariant,
  MaterialIcon,
  MaterialIconSize
} from '@ampcore/adel-react'
import { Loading } from '@ampcore/adel-react'
import { Notification } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { LocationService } from '@ampcore/client-side-services'
import React, { useEffect } from 'react'
import xss from 'xss'

import { NotificationType } from '../../main'
import { buildAdvisorSearchUrl } from './buildAdvisorSearchUrl'

export type DynamicAdvisorProspectProps = {
  testId?: string
  prospectContent?: string
  disclosure?: string
  prospectLabels: {
    heading: string
    icon: BrandIconId
    form: string
    placeholder?: string
    analytics?: string
    button?: string
  }
}

export const DynamicAdvisorProspect: React.FC<
  DynamicAdvisorProspectProps
> = props => {
  const { testId, prospectLabels, prospectContent, disclosure } = props

  const locationService = LocationService.getInstance()
  const [isInvalidZip, setIsInvalidZip] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [geolocationFailed, setGeolocationFailed] = React.useState(false)
  const zipRef = React.useRef<HTMLInputElement>(null)
  const searchButtonRef = React.useRef<HTMLButtonElement>(null)

  const validateZip = (zip: string) => {
    return /^\d{5}(-\d{4})?$/.test(zip)
  }

  useEffect(() => {
    // Prefill zip code if possible
    const prefillZip = async () => {
      try {
        const resp = locationService.getPostalCodeFromStorage()
        if (resp && validateZip(resp)) {
          zipRef.current!.value = resp
        }
      } catch {
        console.error(
          'Invalid postal code in storage or error accessing storage'
        )
      }
    }
    prefillZip()
  }, [locationService])

  const handleFindMyLocation = async () => {
    try {
      setGeolocationFailed(false)
      setIsLoading(true)
      const resp = await locationService.getPostalCode()
      if (!resp || !validateZip(resp)) {
        throw new Error('Invalid postal code received')
      }
      setIsInvalidZip(false)
      zipRef.current!.value = resp
      searchButtonRef.current!.focus()
    } catch {
      setGeolocationFailed(true)
      console.error('Error fetching postal code in dynamic advisor component')
      zipRef.current?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  // TODO add analytic pings
  const handleSearchClick = () => {
    const isValidZip = validateZip(zipRef.current?.value ?? '')
    if (isValidZip) {
      setIsInvalidZip(false)
      const url = buildAdvisorSearchUrl({ zip: zipRef.current?.value ?? '' })
      window.open(url, '_blank')
    } else {
      setIsInvalidZip(true)
      zipRef.current?.focus()
    }
  }

  return (
    <div
      className="DynamicAdvisor DynamicAdvisor--prospectView u-posRelative u-bgColorMidnightBlue"
      style={{
        backgroundImage: `url(${window.AMPCOMPATH}images/facets/hero-facets-dark-midnight-blue.png)`
      }}
      data-analytics-container="Advisor"
      data-testid={testId}
    >
      <div className="DynamicAdvisor-content u-posRelative u-sizeConstrained u-paddingVert32">
        <div className={'Grid u-lg-padding20'}>
          <div className="DynamicAdvisor-heading u-lg-size5of12 u-lg-after1of12 u-flexAlignContentCenter">
            {prospectLabels.icon && (
              <BrandIcon
                icon={prospectLabels.icon}
                size={BrandIconSize.MEDIUM}
                className="u-paddingBottom8 u-fillColorWhite u-md-hidden u-sm-hidden"
                testId={testId ? `${testId}-icon` : undefined}
              />
            )}
            <h3
              className="u-textSecondary u-marginBottom20 u-textColorWhite u-paddingRight8"
              dangerouslySetInnerHTML={{ __html: prospectLabels.heading }}
            ></h3>
          </div>
          <div className="u-sm-sizeFull u-lg-size1of2">
            <div
              className={
                'DynamicAdvisor-searchForm DynamicAdvisor-framedSearchForm u-sm-border0 u-padding20 u-bgColorWhite'
              }
            >
              {geolocationFailed && (
                <Notification
                  type={NotificationType.ALERT}
                  dismissable={false}
                  message="Geolocation is temporarily unavailable. Please enter a ZIP code instead."
                  className="u-marginBottom20"
                />
              )}
              <div className="u-flex u-lg-flexRow u-md-flexRow u-sm-flexCol">
                <label
                  htmlFor="DynamicAdvisor-input"
                  className="Input-label u-lg-size1of2 u-md-size1of2 u-textBold u-paddingTop4 u-textSizeMsrp1 u-textDefault u-sm-marginBottom16 u-paddingRight20 u-sm-paddingRight0"
                >
                  {prospectLabels.form}
                </label>
                <div className="u-flex u-sm-flexWrap">
                  <div className="u-flex u-flexCol u-sm-sizeFull">
                    <div className="Form-group u-marginBottom0">
                      <div className="Input-group">
                        <input
                          id="DynamicAdvisor-input"
                          className={
                            `Input Input--floatingLabel` +
                            (isInvalidZip ? ' Input--error' : '')
                          }
                          placeholder=""
                          type="text"
                          ref={zipRef}
                          onChange={e => {
                            if (validateZip(e.target.value)) {
                              setIsInvalidZip(false)
                            }
                          }}
                          data-testid={testId ? `${testId}-input` : undefined}
                        />
                        <label
                          className="Input-label"
                          htmlFor="DynamicAdvisor-input"
                        >
                          {prospectLabels.placeholder}
                        </label>
                      </div>
                      {isInvalidZip && (
                        <ul id="example_input_error-error-help">
                          <li className="Form-help Form-help--error u-marginTop4">
                            <MaterialIcon
                              icon={AmpcoreMaterialIconId.WARNING}
                              size={MaterialIconSize.SIZE_18}
                              className="u-textColorAlert"
                            />
                            <span className="u-hiddenVisually">Error,</span>{' '}
                            Enter a valid 5-digit ZIP code
                          </li>
                        </ul>
                      )}
                    </div>
                    <div className="u-marginVert8 u-flexAlignItemsStart u-paddingLeft0">
                      <div className="Content">
                        <Button
                          onClick={handleFindMyLocation}
                          variant={ButtonVariant.LINK}
                          className="DynamicAdvisor-findMyLocation"
                          aria-label="Find my location and perform a search for advisors near me"
                        >
                          Find my location
                        </Button>
                      </div>
                      {isLoading && (
                        <div
                          className="u-posRelative u-marginLeft24 u-marginTop8"
                          aria-label="Finding your location"
                        >
                          <Loading
                            modifiers="small"
                            className="u-posAbsoluteCenter u-textColorBlack"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    {/** AdvisorProspect-button used for analytics */}
                    <Button
                      className="AdvisorProspect-button u-marginLeft16 u-sm-marginLeft0 u-sm-marginTop20 u-sm-marginBottom20 u-sm-sizeFull"
                      data-analytics={prospectLabels.analytics}
                      onClick={handleSearchClick}
                      ref={searchButtonRef}
                      testId={testId ? `${testId}-search-button` : undefined}
                    >
                      {prospectLabels.button}
                    </Button>
                  </div>
                </div>
              </div>
              {prospectContent && (
                <div
                  className={
                    'u-marginTop8 u-marginBottom8 u-borderTop u-paddingTop8'
                  }
                  dangerouslySetInnerHTML={{
                    __html: xss(prospectContent)
                  }}
                  data-testid={
                    testId ? `${testId}-prospect-content` : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
        {disclosure && (
          <div className="u-paddingTop8 u-lg-size1of2 u-lg-before1of2">
            <div
              className="DynamicAdvisor-disclosure Content--reversed"
              dangerouslySetInnerHTML={{ __html: xss(disclosure) }}
              data-testid={testId ? `${testId}-disclosure` : undefined}
            />
          </div>
        )}
      </div>
    </div>
  )
}
